import { Component, AfterViewInit, ViewChild, NgZone, ElementRef } from '@angular/core';
import { TvFeature } from 'src/app/data/enum/tv-feature';
import setupInitialPageTimeline, {
  remoteControlTimeline,
} from 'src/app/utils/animation/initial-page-timeline';
import gsap from 'gsap';
import { RemoteControlService } from 'src/app/service/remote-control.service';
import FeaturePage from 'src/app/core/feature-page';
import { LocalizationService } from 'src/app/service/localization.service';
import { slidersData } from 'src/app/data/browse-title-sliders';
import { RemoteButtonKey, RemoteButtonKeyCode } from 'src/app/data/type/remote-button';
import endPageTimeline from '../../utils/animation/end-page-timeline';
import { ProgressService } from 'src/app/service/progress.service';
import { AnalyticsEvent, triggerEvent } from 'src/app/utils/analytics';
import { ParameterService } from 'src/app/service/parameter.service';

@Component({
  selector: 'app-browse-titles-page',
  templateUrl: './browse-titles-page.component.html',
  styleUrls: ['./browse-titles-page.component.scss'],
})
export class BrowseTitlesPageComponent extends FeaturePage implements AfterViewInit {
  @ViewChild('browseTitlesRef') containerElementRef: ElementRef;
  private tvIntroTimeline: GSAPTimeline;
  private currentSliderIndex = 0;
  private c08SliderHeight: number = 0;
  public slidersData = slidersData;
  public availableControlButtons: Array<RemoteButtonKeyCode> = [];
  public showSecondTooltip: boolean = false;

  constructor(
    protected localizationService: LocalizationService,
    protected remoteControlService: RemoteControlService,
    protected progressService: ProgressService,
    protected zone: NgZone,
    protected parameterService: ParameterService,
  ) {
    super(
      localizationService,
      remoteControlService,
      parameterService,
      zone,
      TvFeature.BROWSE_TITLES,
    );
  }

  handleLastStep() {
    endPageTimeline(this.containerElementRef).play();
  }

  setAvailableButtons(clickedButton: RemoteButtonKey) {
    let tvFrameTranslateY = 0;

    if (clickedButton === RemoteButtonKey.up && this.currentSliderIndex > 0) {
      this.currentSliderIndex--;
      tvFrameTranslateY = this.currentSliderIndex * -this.c08SliderHeight;
    }

    if (
      clickedButton === RemoteButtonKey.down &&
      this.currentSliderIndex < slidersData.length - 1
    ) {
      this.currentSliderIndex++;
      tvFrameTranslateY = this.currentSliderIndex * -this.c08SliderHeight;
    }

    const activeSlider = this.slidersData[this.currentSliderIndex];
    const activeSliderIndex = activeSlider.activeIndex || 0;

    const showUpButton = this.currentSliderIndex > 0;
    const showDownButton = this.currentSliderIndex < this.slidersData.length - 1;
    const showLeftButton = activeSliderIndex > 0;
    const showRightButton = activeSliderIndex < activeSlider.slides.length - 1;

    this.availableControlButtons = [
      ...this.currentStep.activeButtonKeys,
      ...(showRightButton ? [RemoteButtonKey.right] : []),
      ...(showLeftButton ? [RemoteButtonKey.left] : []),
      ...(showUpButton ? [RemoteButtonKey.up] : []),
      ...(showDownButton ? [RemoteButtonKey.down] : []),
    ];

    this.slidersData.forEach((slider, index) => {
      slider.slides.forEach((slide) => (slide.active = false));
      slider.isActive = index === this.currentSliderIndex;
      if (slider.activeIndex === null) slider.activeIndex = 0;
      if (index === this.currentSliderIndex) {
        slider.slides[slider.activeIndex || 0].active = true;
      }
    });

    if (clickedButton !== RemoteButtonKey.left && clickedButton !== RemoteButtonKey.right)
      gsap.to('.tv-frame-content', {
        y: tvFrameTranslateY,
      });
  }

  animateSlider(clickedButton: RemoteButtonKey) {
    const slider = this.slidersData[this.currentSliderIndex];
    if (clickedButton === RemoteButtonKey.left && slider.activeIndex - 1 >= 0) {
      slider.activeIndex--;
    } else if (
      clickedButton === RemoteButtonKey.right &&
      slider.activeIndex + 1 <= slider.slides.length - 1
    ) {
      slider.activeIndex++;
    }
    slider.slides.forEach((slide, index) => {
      slide.active = index === slider.activeIndex;
    });
  }

  setC08SliderHeight(height: number) {
    this.c08SliderHeight = height;
  }

  handleRemoteSignal(signal: RemoteButtonKey) {
    if (
      this.stepIndex === 0 &&
      (signal === RemoteButtonKey.center || signal === RemoteButtonKey['center-hisense'])
    ) {
      this.isRemoteDisabled = true;
      this.nextStep();
      triggerEvent(AnalyticsEvent.browseTitlesComplete);
    } else if (
      (this.stepIndex === 0 && signal === RemoteButtonKey.down) ||
      signal === RemoteButtonKey.up
    ) {
      this.showSecondTooltip = true;
      this.setAvailableButtons(signal);
      triggerEvent(AnalyticsEvent.browseTitlesFreeplay);
    } else if (signal === RemoteButtonKey.left || signal === RemoteButtonKey.right) {
      this.showSecondTooltip = true;
      this.animateSlider(signal);
      this.setAvailableButtons(signal);
      triggerEvent(AnalyticsEvent.browseTitlesFreeplay);
    }
  }

  setupIntroTvTimeline(): GSAPTimeline {
    const tl = gsap.timeline({ paused: true, defaults: { duration: 1.2, ease: 'power1.inOut' } });
    const introElements = ['[data-animation="intro"]', '[data-animation="intro-copy"]'];

    tl.to('[data-animation="intro-header-menu"]', { y: '-100%', autoAlpha: 0 }, 0)
      .to(introElements, { autoAlpha: 0 }, 0)
      .to('[data-animation="apps"]', { y: '-25%' }, 0)
      .addLabel('scene2', '+=0.5')
      .to(['[data-animation="content-wrapper"]'], { y: '-75%' }, 'scene2')
      .to('.tv-sliders-wrapper', { y: '-65%' }, 'scene2')
      .fromTo(
        '[data-animation="apps"]',
        { autoAlpha: 1 },
        { autoAlpha: 0, duration: 0.5 },
        'scene2',
      )
      .to('[data-animation="apps-highlighted"]', { autoAlpha: 1 }, 'scene2')
      .addLabel('scene3', '-=0.1')

      .to(
        ['[data-animation="content-wrapper"]', '.tv-sliders-wrapper'],
        { y: '-=25%', duration: 0.5 },
        'scene3',
      )
      .to('[data-animation="apps"]', { autoAlpha: 1 }, 'scene3')
      .to(
        '[data-animation="apps-highlighted"]',
        {
          autoAlpha: 0,
          duration: 0.5,
          onComplete: () => {
            this.zone.run(() => {
              (this.slidersData[0].isActive = true), (this.slidersData[0].slides[0].active = true);
              this.isRemoteDisabled = false;
              this.setAvailableButtons(RemoteButtonKey.center);
            });
          },
        },
        'scene3',
      )
      .add(remoteControlTimeline(this.isAbstract))
      .timeScale(1.5);
    return tl;
  }

  ngAfterViewInit(): void {
    if (this.progressService.getHasDoneFirstNavigation()) {
      this.containerElementRef.nativeElement.classList.add('invisible');
      setTimeout(() => super.ngAfterViewInit(), 0);
    } else {
      this.progressService.setHasDoneFirstNavigation();
    }

    this.tvIntroTimeline = this.setupIntroTvTimeline();
    const timeline = setupInitialPageTimeline(
      this.containerElementRef,
      this.isAbstract,
      true,
    ).play();
    timeline.then(() => this.tvIntroTimeline.play());
  }
}
